import SearchIcon from "@mui/icons-material/Search";
import { Box, TextField, styled } from "@mui/material";
import moment from "moment";
import { ReactNode, useEffect, useState } from "react";
import { HeaderFormatEnum } from "../../../components/common-table/enum/header-format";
import TableComponent, {
    ITableColumn,
} from "../../../components/common-table/table-component";
import { displayDateFormat } from "../../../constants";
import useDebounce from "../../../hooks/use-debounce";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import {
    ListBuilderTableColumns,
    SortableListBuilderColumns,
} from "../enum/list-builder-table-columns";
import { IListBuilderTableDto } from "../interface/dto/list-builder-table-list.dto";
import {
    listBuilderTableSetPaginationAction,
    listBuilderTableSetSearchQueryAction,
    listBuilderTableSetSortingAction,
} from "../redux/list-builder-table-page.action";
import { ITEMS_PER_PAGE_ALL } from "../selectors/filters-and-pagination";
import { listBuilderTableColumns } from "../service/list-builder-table-columns";
import DeleteButton from "./delete-button";
import ListBuilderBuild from "./list-builder-build";
import ListBuilderDownload from "./list-builder-download";
import ListBuilderDuplicate from "./list-builder-duplicate";
import ListBuilderEdit from "./list-builder-edit";
import { ListBuilderStatus } from "./list-builder-status";
import UserDropdown from "./user-dropdown";

const TABLE_COLUMNS = [
    ...listBuilderTableColumns.map((column) => ({
        title: column.name,
        id: column.id,
        sortable: column.sortable,
        width: column.width,
        icon: column.icon,
    })),
];

const ListBuilderTable = ({ className }: IWithClassName) => {
    const dispatch = useAppDispatch();
    const [query, setQuery] = useState<string | undefined>(undefined);
    const { listBuilders, pagination, isLoading, searchQuery } = useAppSelector(
        (state) => state.listBuilderTable.listBuilderTable,
    );
    const debouncedQuery = useDebounce(query);

    useEffect(() => {
        if (query === undefined && searchQuery !== query) {
            setQuery(searchQuery);
        }
    }, [searchQuery, query]);

    useEffect(() => {
        if (query !== undefined && debouncedQuery !== searchQuery) {
            dispatch(
                listBuilderTableSetSearchQueryAction(debouncedQuery ?? ""),
            );
        }
    }, [debouncedQuery, dispatch, searchQuery]);

    const handleRowsPerPageChange = (rows: number) => {
        dispatch(
            listBuilderTableSetPaginationAction(pagination.currentPage, rows),
        );
    };

    const handlePageChange = (page: number) => {
        dispatch(
            listBuilderTableSetPaginationAction(page, pagination.itemsPerPage),
        );
    };

    const handleColumnOrder = (
        columnIndex: number,
        column: ITableColumn,
        direction: "asc" | "desc",
    ) => {
        dispatch(
            listBuilderTableSetSortingAction(
                column.id as SortableListBuilderColumns,
                direction,
            ),
        );
    };

    const getCellContent = (
        listBuilder: IListBuilderTableDto,
        columnKey: string,
    ) => {
        switch (columnKey) {
            case ListBuilderTableColumns.LIST_BUILDER_NAME:
                return listBuilder.listBuilderName;

            case ListBuilderTableColumns.STATUS:
                return <ListBuilderStatus listBuilder={listBuilder} />;

            case ListBuilderTableColumns.LAST_BUILD:
                return listBuilder.lastRunDate
                    ? moment
                          .utc(listBuilder.lastRunDate)
                          .format(displayDateFormat)
                    : "-";

            case ListBuilderTableColumns.BUILD_LIST_BUTTON:
                return <ListBuilderBuild listBuilder={listBuilder} />;

            case ListBuilderTableColumns.DOWNLOAD_BUTTON:
                return <ListBuilderDownload listBuilder={listBuilder} />;

            case ListBuilderTableColumns.EDIT_BUTTON:
                return <ListBuilderEdit listBuilder={listBuilder} />;

            case ListBuilderTableColumns.DUPLICATE_BUTTON:
                return <ListBuilderDuplicate listBuilder={listBuilder} />;

            case ListBuilderTableColumns.DELETE_BUTTON:
                return <DeleteButton listBuilder={listBuilder} />;

            default:
                return (
                    listBuilder[
                        columnKey as keyof IListBuilderTableDto
                    ]?.toString() ?? null
                );
        }
    };

    const renderCell = (rowIndex: number, columnIndex: number): ReactNode => {
        const adjustedRowIndex =
            pagination.itemsPerPage === ITEMS_PER_PAGE_ALL
                ? rowIndex
                : rowIndex % pagination.itemsPerPage;

        const listBuilder = listBuilders?.result?.[adjustedRowIndex];
        if (!listBuilder) return null;

        const columnKey = TABLE_COLUMNS[columnIndex].id;
        const cellContent = getCellContent(listBuilder, columnKey);
        return <span className={className}> {cellContent} </span>;
    };

    const renderNoDataMessage = () => {
        if (query && query.trim().length > 0) {
            return `No results found for "${query}". Try different search criteria.`;
        }
        return "Try creating a list builder";
    };

    return (
        <Box className={className}>
            <TableComponent
                columns={TABLE_COLUMNS}
                headerFormat={HeaderFormatEnum.PageSizeLeft}
                rowsPerPage={pagination.itemsPerPage}
                totalRows={listBuilders?.total ?? 0}
                currentPage={pagination.currentPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={handlePageChange}
                onColumnOrder={handleColumnOrder}
                renderCell={renderCell}
                renderNoDataMessage={renderNoDataMessage}
                isLoading={isLoading}
                uniqueRowId={(rowIndex) => `${rowIndex}`}
                customHeader={
                    <>
                        <div className="user-dropdown-container">
                            <UserDropdown className={className} />
                        </div>
                        <div className="search-box-placeholder">
                            <TextField
                                variant="standard"
                                value={query ?? ""}
                                onChange={({ currentTarget }) =>
                                    setQuery(currentTarget.value)
                                }
                                InputProps={{
                                    endAdornment: (
                                        <SearchIcon className="search-icon" />
                                    ),
                                }}
                            />
                        </div>
                    </>
                }
            />
        </Box>
    );
};

export default styled(ListBuilderTable)(({ theme }: IWithThemeProps) => ({
    ".user-dropdown-container": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    ".search-icon": {
        color: theme?.palette.text.primary,
    },
}));
